/**
 * /data-export — Habeas Data (Ley 1581/2012): solicitar export de la
 * data del tenant y hacer polling del estado hasta que el ZIP + signed
 * URL estén listos.
 *
 * Backend:
 *   POST /api/v1/admin/partners/me/data-export-request        → 202 {export_id}
 *   GET  /api/v1/admin/partners/me/data-export-request/:id    → status
 *
 * El backend no expone listado de solicitudes — solo GET por id — así
 * que la página trackea la solicitud creada en esta sesión.
 */

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, Clock, Download, FileArchive, XCircle } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { partnerApi, type DataExportKind, type DataExportStatus } from '@/lib/api'

const KIND_OPTIONS: { value: DataExportKind; label: string; desc: string }[] = [
  { value: 'full', label: 'Completo', desc: 'Toda la data del tenant: documentos, audit log y datos personales.' },
  { value: 'accounting', label: 'Contable', desc: 'Documentos electrónicos y soportes contables.' },
  { value: 'audit', label: 'Audit log', desc: 'Historial de aceptaciones legales y eventos de auditoría.' },
  { value: 'personal', label: 'Datos personales (PII)', desc: 'Solo la información personal registrada (Ley 1581/2012).' },
]

const TERMINAL_STATUSES = ['ready', 'failed', 'expired']

export function DataExportPage() {
  const [kind, setKind] = useState<DataExportKind>('full')
  const [legalReason, setLegalReason] = useState('')
  const [exportId, setExportId] = useState<string | null>(null)

  const request = useMutation({
    mutationFn: () => partnerApi.requestDataExport(kind, legalReason.trim() || undefined),
    onSuccess: (r) => {
      setExportId(r.export_id)
      toast.success('Solicitud registrada. Preparando el export…')
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } }
      toast.error(`No se pudo solicitar el export: ${e?.response?.data?.message ?? 'error desconocido'}`)
    },
  })

  const status = useQuery({
    queryKey: ['data-export-status', exportId],
    queryFn: () => partnerApi.getDataExportStatus(exportId!),
    enabled: !!exportId,
    // Polling cada 5s mientras no esté en estado terminal.
    refetchInterval: (query) => {
      const s = query.state.data?.status
      return s && TERMINAL_STATUSES.includes(s) ? false : 5_000
    },
  })

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold">Export de datos</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Bajo la Ley 1581/2012 (Habeas Data) puedes solicitar un export de la
          data de tu cuenta. El archivo se genera en background y recibirás un
          enlace de descarga temporal (24 h). Típicamente tarda menos de 5 minutos.
        </p>

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <p className="text-sm font-semibold">1. Alcance del export</p>
          <div className="mt-3 space-y-2">
            {KIND_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                  kind === opt.value ? 'border-brand-500 bg-brand-50/50' : 'border-border hover:bg-bg-elevated'
                }`}
              >
                <input
                  type="radio"
                  name="export-kind"
                  value={opt.value}
                  checked={kind === opt.value}
                  onChange={() => setKind(opt.value)}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-medium">{opt.label}</span>
                  <span className="block text-xs text-fg-secondary">{opt.desc}</span>
                </span>
              </label>
            ))}
          </div>

          <label className="mt-4 block text-sm font-medium" htmlFor="legal-reason">
            Motivo legal (opcional)
          </label>
          <input
            id="legal-reason"
            type="text"
            value={legalReason}
            maxLength={200}
            onChange={(e) => setLegalReason(e.target.value)}
            placeholder="p. ej. rectificación de datos"
            className="mt-1.5 w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />

          <button
            type="button"
            onClick={() => request.mutate()}
            disabled={request.isPending}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FileArchive className="h-4 w-4" />
            {request.isPending ? 'Solicitando…' : 'Solicitar export'}
          </button>
        </div>

        {exportId && (
          <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
            <p className="text-sm font-semibold">2. Estado de la solicitud</p>
            <p className="mt-1 font-mono text-xs text-fg-muted">ID: {exportId}</p>

            {status.isError && (
              <p className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
                No se pudo consultar el estado del export. Reintenta en unos segundos.
              </p>
            )}
            {status.data && <ExportStatusCard s={status.data} />}
          </div>
        )}
      </div>
    </AppShell>
  )
}

function ExportStatusCard({ s }: { s: DataExportStatus }) {
  if (s.status === 'ready') {
    return (
      <div className="mt-4 rounded-md border border-green-300 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
        <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-900 dark:text-green-100">
          <CheckCircle2 className="h-4 w-4" /> Export listo
        </p>
        {s.size_bytes != null && (
          <p className="mt-1 text-xs text-green-800 dark:text-green-200">
            Tamaño: {(s.size_bytes / 1e6).toLocaleString('es-CO', { maximumFractionDigits: 2 })} MB
          </p>
        )}
        {s.signed_url ? (
          <a
            href={s.signed_url}
            target="_blank"
            rel="noopener"
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Download className="h-4 w-4" /> Descargar ZIP
          </a>
        ) : (
          <p className="mt-2 text-xs text-green-800 dark:text-green-200">
            El enlace de descarga llegará por email.
          </p>
        )}
        {s.signed_url_expires_at && (
          <p className="mt-2 text-xs text-green-800 dark:text-green-200">
            El enlace expira: {new Date(s.signed_url_expires_at).toLocaleString('es-CO')}
          </p>
        )}
      </div>
    )
  }
  if (s.status === 'failed') {
    return (
      <p className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
        <XCircle className="h-4 w-4" /> El export falló. Solicítalo de nuevo o escríbenos a soporte.
      </p>
    )
  }
  return (
    <p className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-100">
      <Clock className="h-4 w-4 animate-pulse" />
      En proceso ({s.status})… esta página se actualiza sola cada 5 segundos.
    </p>
  )
}
